import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

const DEFAULT_MARGIN = '200px';

/**
 * True while the host node is in (or near) the viewport. Native and
 * environments without IntersectionObserver stay eager so previews still
 * show. Used to unmount decoded images, video players, and PDF iframes
 * once they scroll away — those buffers, not the JS event log, are what
 * push a long Work thread toward a gigabyte.
 */
export function useOnScreen({
  rootMargin = DEFAULT_MARGIN,
  eager = false,
} = {}) {
  const ref = useRef(null);
  const [isOnScreen, setIsOnScreen] = useState(() => eager || Platform.OS !== 'web');

  useEffect(() => {
    if (eager || Platform.OS !== 'web') {
      setIsOnScreen(true);
      return undefined;
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsOnScreen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => setIsOnScreen(entries.some((entry) => entry.isIntersecting)),
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [eager, rootMargin]);

  return [ref, isOnScreen];
}
